/**
 * Shopify Cart Synchronization Helper
 * 
 * Handles communication between chat widget iframe and parent Shopify shop.
 * 
 * Debug Logging:
 * To enable debug logs, set window.CART_SYNC_DEBUG = true in the browser console.
 * This will show all PostMessage communication with the parent shop.
 */
export const ShopifyCartSync = {
  // Debug logging helper - optimized with early return
  debugLog(...args) {
    if (!window.CART_SYNC_DEBUG) return;
    console.log('[ShopifyCartSync]', ...args);
  },
  
  debugWarn(...args) {
    if (!window.CART_SYNC_DEBUG) return;
    console.warn('[ShopifyCartSync]', ...args);
  },
  // Initialize cart sync handlers
  init() {
    // Only initialize if we're in an iframe
    if (window.self === window.top) return;
    
    // Listen for cart update messages from parent
    window.addEventListener('message', this.handleParentMessage.bind(this));
  },

  // Send cart update to parent shop
  updateParentCart(cartData) {
    if (window.self === window.top) return;

    const message = {
      type: 'COMMSLAYER_CART_UPDATE',
      action: 'updateCart',
      data: cartData
    };

    // Post message to parent window
    try {
      window.parent.postMessage(message, '*');
    } catch (error) {
      this.debugWarn('⚠️ Failed to send cart update to parent:', error);
    }
  },

  // Add items to parent shop cart
  addToParentCart(items, cartId = null) {
    if (window.self === window.top) return;

    const message = {
      type: 'COMMSLAYER_CART_UPDATE',
      action: 'addItems',
      data: {
        items: items, // Array of {variant_id, quantity}
        cart_id: cartId // Storefront cart ID to sync
      }
    };

    try {
      window.parent.postMessage(message, '*');
    } catch (error) {
      this.debugWarn('⚠️ Failed to add items to parent cart:', error);
    }
  },

  // Open cart drawer/modal in parent shop
  openParentCart() {
    if (window.self === window.top) return;

    const message = {
      type: 'COMMSLAYER_CART_UPDATE',
      action: 'openCart'
    };

    try {
      window.parent.postMessage(message, '*');
    } catch (error) {
      this.debugWarn('⚠️ Failed to open parent cart:', error);
    }
  },

  // Navigate to checkout in parent shop
  navigateToCheckout(checkoutUrl) {
    if (window.self === window.top) return;

    const message = {
      type: 'COMMSLAYER_CART_UPDATE',
      action: 'goToCheckout',
      data: {
        checkoutUrl: checkoutUrl
      }
    };

    try {
      window.parent.postMessage(message, '*');
    } catch (error) {
      this.debugWarn('⚠️ Failed to navigate to checkout:', error);
    }
  },

  // Handle messages from parent shop
  handleParentMessage(event) {
    // Validate message format
    if (!event.data || event.data.type !== 'SHOPIFY_CART_STATE') return;
    
    this.debugLog('📥 Received message from parent shop:', event.data);
    
    // Handle cart state updates from parent (now consistent format)
    const cart = event.data.cart;
    const ajaxToken = event.data.ajax_token;
    
    if (cart && ajaxToken) {
      this.debugLog('✅ Cart sync received with token:', ajaxToken);
      
      // Emit event that Vue components can listen to
      window.dispatchEvent(new CustomEvent('shopifyCartUpdated', {
        detail: {
          cart: cart,
          ajax_token: ajaxToken
        }
      }));
    } else {
      this.debugWarn('⚠️ Cart message missing data:', { cart: !!cart, token: !!ajaxToken });
    }
  },
  
  forwardCartSyncToParent(syncData) {
    if (window.self === window.top) return;

    this.debugLog('📤 Forwarding cart sync to parent shop:', syncData);

    const message = {
      ...syncData,
      type: 'CART_SYNC'
    };

    try {
      window.parent.postMessage(message, '*');
    } catch (error) {
      this.debugWarn('⚠️ Failed to forward cart sync to parent:', error);
    }
  },

  // Request current cart state from parent
  requestCartState() {
    if (window.self === window.top) return;

    this.debugLog('📤 Requesting cart state from parent shop');
    const message = {
      type: 'COMMSLAYER_CART_UPDATE',
      action: 'getCart'
    };

    try {
      window.parent.postMessage(message, '*');
    } catch (error) {
      this.debugWarn('⚠️ Failed to request cart state from parent:', error);
    }
  }
};

export default ShopifyCartSync;